import { useEffect, useMemo, useState } from 'react';

import { AccountSelector as NovaAccountSelector, type SelectedAccount } from '../../../account-selector';
import { useChatOverlayContext } from '../../contexts/chat-overlay-context';

interface AccountSelectorProps {
  className?: string;
  width?: number;
  placeholder?: string;
}

/**
 * Component for displaying and selecting the current account.
 * Uses chat overlay context to access account state.
 * Mimics the behavior from the old chat overlay:
 * - Shows selector for new chats when feature flag is enabled
 * - Shows selector for existing chats when no account is set (allows setting account after chat creation)
 * - Shows account name when account is set
 * - Updates account context when account is selected
 * - Triggers GraphQL mutation when account is selected after messages have been sent
 */
export function AccountSelector({ className, width, placeholder }: AccountSelectorProps) {
  const {
    accountUuid,
    accountName,
    selectedAccount: contextSelectedAccount,
    onSelectAccount,
    pageAccountUuid,
    isStandaloneMode,
    displayAccount,
    userUuid,
    userScopes,
    hasAnyMessages,
    canSelectAccount,
  } = useChatOverlayContext();

  const [selectedAccount, setSelectedAccount] = useState<SelectedAccount>(contextSelectedAccount);

  // Determine which account to display (priority: chat data > stored context > current page context)
  const accountToDisplay = useMemo(() => {
    return (
      displayAccount ??
      (accountUuid && accountName
        ? {
            uuid: accountUuid,
            name: accountName,
          }
        : null)
    );
  }, [displayAccount, accountUuid, accountName]);

  // Derive account locked state: account is locked once there are messages and an account has been selected
  const isAccountLocked = useMemo(() => {
    const hasAccountContext = Boolean(accountToDisplay?.uuid);
    const hasSelectedAccount = Boolean(selectedAccount?.uuid);
    return hasAnyMessages && (hasAccountContext || hasSelectedAccount);
  }, [hasAnyMessages, accountToDisplay?.uuid, selectedAccount?.uuid]);

  const shouldShowAccountSelector = useMemo(() => {
    if (!canSelectAccount) {
      return false;
    }

    if (isStandaloneMode) {
      return !isAccountLocked;
    }

    if (pageAccountUuid) {
      return false;
    }

    return !isAccountLocked;
  }, [canSelectAccount, isAccountLocked, isStandaloneMode, pageAccountUuid]);

  useEffect(() => {
    if (!shouldShowAccountSelector) {
      setSelectedAccount(undefined);
    }
  }, [shouldShowAccountSelector]);

  const handleAccountSelect = (account: SelectedAccount) => {
    setSelectedAccount(account);
    onSelectAccount(account);
  };

  if (shouldShowAccountSelector && userUuid && userScopes) {
    return (
      <div className={className}>
        <NovaAccountSelector
          width={width}
          selectedAccount={selectedAccount}
          onSelectAccount={handleAccountSelect}
          loggedInUserUuid={userUuid}
          userScopes={userScopes}
          placeholder={placeholder}
        />
      </div>
    );
  }

  // Show label when account is locked or when account is set (for existing chats with account)
  const displayName = accountToDisplay?.name ?? selectedAccount?.label;

  if (displayName) {
    return (
      <div className={className}>
        <span className="text-muted-foreground text-sm">
          <span className="font-semibold">Account</span>: {displayName}
        </span>
      </div>
    );
  }
}
